import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faClose,
  faEye,
  faEyeSlash,
  faInfoCircle,
  faList,
} from '@fortawesome/sharp-solid-svg-icons';
import { useRouter } from 'next/navigation';
import React, { useEffect, useRef } from 'react';

import Button, { ButtonSize, ButtonVariant } from '@/components/button';
import IconButton from '@/components/button/iconButton';
import Typography from '@/components/typography';
import { LAYERS_URL } from '@/constants/urls';
import { ArdaLayerClass } from '@/features/layerLibrary/models';
import { useLayerLibraryContext } from '@/features/layerLibrary/provider';
import { LayerLibraryOverlayLegend } from '@/features/navigation/bottom-navigation/toolkit/layerLibraryOverlay/legend';

type Props = {
  layer: ArdaLayerClass;
};

export const LayerLibraryOverlayCard: React.FC<Props> = ({ layer }) => {
  const router = useRouter();
  const { disableLayer, legendLayer, setLegendLayerId } =
    useLayerLibraryContext();

  const cardRef = useRef<HTMLDivElement>(null);
  const legendIsOpen = legendLayer?.id === layer.id;

  useEffect(() => {
    if (!legendIsOpen || !cardRef.current) return;

    cardRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [legendIsOpen]);

  return (
    <div
      ref={cardRef}
      className="flex flex-col gap-y-1 rounded-md bg-white p-2 shadow-md"
    >
      {/* Header section */}
      <div className="flex items-center justify-between truncate">
        <Typography className="truncate" title={layer.name} variant="button2">
          {layer.name}
        </Typography>
        <div className="flex items-center">
          <IconButton
            size={ButtonSize.SMALL}
            variant={ButtonVariant.GHOST}
            onClick={() => {
              if (layer.enabled) {
                layer.disable();
              } else {
                layer.enable();
              }
            }}
          >
            {layer.enabled && (
              <FontAwesomeIcon className="text-green-50" icon={faEye} />
            )}
            {!layer.enabled && (
              <FontAwesomeIcon className="text-gray-70" icon={faEyeSlash} />
            )}
          </IconButton>
          <IconButton
            size={ButtonSize.SMALL}
            variant={ButtonVariant.GHOST}
            onClick={() => disableLayer(layer.id)}
          >
            <FontAwesomeIcon className="text-gray-70" icon={faClose} />
          </IconButton>
        </div>
      </div>

      {/* Button Section */}
      <div className="flex h-[32px] gap-x-1">
        <Button
          className="flex flex-1 items-center gap-x-1 rounded bg-gray-10 text-gray-100 hover:bg-gray-20"
          size={ButtonSize.SMALL}
          variant={ButtonVariant.GHOST}
          onClick={() => {
            if (legendLayer) {
              setLegendLayerId(null);
            } else {
              setLegendLayerId(layer.id);
            }
          }}
        >
          <FontAwesomeIcon className="text-gray-60" icon={faList} />
          Legend
        </Button>

        <Button
          className="flex flex-1 items-center gap-x-1 rounded bg-gray-10 text-gray-100 hover:bg-gray-20"
          size={ButtonSize.SMALL}
          variant={ButtonVariant.GHOST}
          onClick={() => {
            router.push(`${LAYERS_URL}/${layer.id}`);
          }}
        >
          <FontAwesomeIcon className="text-gray-60" icon={faInfoCircle} />
          Info
        </Button>
      </div>

      {legendIsOpen && (
        <div className="mt-2">
          <LayerLibraryOverlayLegend layer={layer} />
        </div>
      )}
    </div>
  );
};
